import { useRef, useState } from 'react';
import {
  Control,
  FormState,
  UseFormSetValue,
  UseFormClearErrors,
  Controller,
  useFieldArray,
  useWatch,
  FieldPath,
} from 'react-hook-form';
import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import * as Yup from 'yup';
import { HERE_APIKEY, LIST_SKUS_URL } from '@constants';
import { useAuthHttp } from '@hooks';
import { AvailableSKU } from '@forms/client/CreateInboundShipmentNoteForm/types';
import {
  SupplierFormValues,
  SupplierLocation,
  emptySupplierContact,
  emptySupplierCertification,
  emptySupplierInsurancePolicy,
  emptySupplierSkuLink,
} from './types';
import { Input } from '../../tailwindComponents/Input';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { Select } from '../../tailwindComponents/Select';
import { CollapsibleFormSection } from '../../tailwindComponents/CollapsibleFormSection';

const iso2Rule = Yup.string().matches(/^[A-Za-z]{2}$/, {
  message: 'Use a 2-letter ISO country code',
  excludeEmptyString: true,
});
const iso3Rule = Yup.string().matches(/^[A-Za-z]{3}$/, {
  message: 'Use a 3-letter ISO currency code',
  excludeEmptyString: true,
});
const decimalRule = Yup.string().matches(/^\d+(\.\d+)?$/, {
  message: 'Enter a number',
  excludeEmptyString: true,
});
const intRule = Yup.string().matches(/^\d+$/, {
  message: 'Enter a whole number',
  excludeEmptyString: true,
});
const optionalEmailRule = Yup.string()
  .transform((value) => (value === '' ? undefined : value))
  .email('Invalid email');

const contactRoleOptions = [
  { label: 'Primary', value: 'Primary' },
  { label: 'Sales', value: 'Sales' },
  { label: 'Operations', value: 'Operations' },
  { label: 'Quality', value: 'Quality' },
  { label: 'Finance', value: 'Finance' },
  { label: 'Legal', value: 'legal' },
  { label: 'Other', value: 'Other' },
];

const certificationStatusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' },
  { label: 'Revoked', value: 'revoked' },
];

const locationTypeOptions = [
  { label: 'Registered', value: 'registered' },
  { label: 'Billing', value: 'billing' },
  { label: 'Shipping', value: 'shipping' },
  { label: 'Headquarters', value: 'headquarters' },
];

const locationSchema = Yup.object().shape({
  locationId: Yup.string(),
  address: Yup.string().required('Address is required'),
  type: Yup.string(),
  line1: Yup.string(),
  line2: Yup.string(),
  city: Yup.string(),
  county: Yup.string(),
  country: Yup.string(),
  postcode: Yup.string(),
  coordinates: Yup.object().shape({
    lon: Yup.number(),
    lat: Yup.number(),
  }),
  isPrimary: Yup.boolean(),
  isActive: Yup.boolean(),
});

export const supplierFormSchema = Yup.object().shape({
  supplierId: Yup.string().required('Supplier ID is required'),
  name: Yup.string()
    .required('Supplier name is required')
    .min(3, 'Supplier name must be at least 3 characters long'),
  type: Yup.string().required('Supplier type is required'),
  contactName: Yup.string().required('Contact name is required'),
  contactEmail: Yup.string()
    .email('Invalid email')
    .required('Contact email is required'),
  contactNumber: Yup.string(),
  hqId: Yup.string(),
  hqName: Yup.string(),
  locations: Yup.array()
    .of(locationSchema)
    .min(1, 'At least one location is required'),
  legalEntity: Yup.object().shape({
    legalName: Yup.string(),
    tradingName: Yup.string(),
    companyRegistrationNumber: Yup.string(),
    vatNumber: Yup.string(),
    taxId: Yup.string(),
    jurisdictionCountry: iso2Rule,
    legalEntityType: Yup.string(),
    incorporationDate: Yup.string(),
  }),
  contacts: Yup.array().of(
    Yup.object().shape({
      contactId: Yup.string(),
      name: Yup.string(),
      email: optionalEmailRule,
      phoneCountryCode: Yup.string(),
      phoneNumber: Yup.string(),
      role: Yup.string(),
      locationId: Yup.string(),
      isActive: Yup.boolean(),
    })
  ),
  corporateHierarchy: Yup.object().shape({
    parentSupplierId: Yup.string(),
    ultimateParentSupplierId: Yup.string(),
    hierarchyLevel: intRule,
    regionalGrouping: Yup.string(),
  }),
  compliance: Yup.object().shape({
    certifications: Yup.array().of(
      Yup.object()
        .shape({
          name: Yup.string(),
          issuingBody: Yup.string(),
          certificateNumber: Yup.string(),
          issueDate: Yup.string(),
          expiryDate: Yup.string(),
          status: Yup.string(),
          documentUrl: Yup.string(),
        })
        .test(
          'expiry-after-issue',
          'Expiry date must be on or after the issue date',
          (certification) => {
            if (!certification?.issueDate || !certification?.expiryDate) {
              return true;
            }
            return certification.expiryDate >= certification.issueDate;
          }
        )
    ),
    insurancePolicies: Yup.array().of(
      Yup.object().shape({
        type: Yup.string(),
        provider: Yup.string(),
        policyNumber: Yup.string(),
        coverageAmount: decimalRule,
        currency: iso3Rule,
        expiryDate: Yup.string(),
        documentUrl: Yup.string(),
      })
    ),
    esgRating: Yup.string(),
    esgAssessmentDate: Yup.string(),
    sanctionsCheckStatus: Yup.string(),
    sanctionsCheckedAt: Yup.string(),
  }),
  suppliedSkus: Yup.array().of(
    Yup.object().shape({
      inventorySkuId: Yup.string(),
      skuCode: Yup.string(),
      productName: Yup.string(),
      supplierSkuCode: Yup.string(),
      leadTimeMeanDays: decimalRule,
      leadTimeStdDays: decimalRule,
      minimumOrderQuantity: decimalRule,
      orderMultiple: decimalRule,
      orderMultipleUnit: Yup.string(),
      incoterms: Yup.string(),
      isPreferred: Yup.boolean(),
      status: Yup.string(),
    })
  ),
  sourceType: Yup.string(),
  brandsSupplied: Yup.string(),
  coverageBranches: Yup.string(),
  certificationStatus: Yup.string(),
  certificationLabel: Yup.string(),
  certificationExpiry: Yup.string(),
  lifecycleStatus: Yup.string(),
  readinessGaps: intRule,
});

const sourceTypeOptions = [
  { label: 'Internal production', value: 'internal production site' },
  { label: 'Contract manufacturer', value: 'contract manufacturer' },
  { label: 'External supplier', value: 'external supplier' },
  { label: 'Co-packer', value: 'co-packer' },
];

const certStatusOptions = [
  { label: 'Valid', value: 'valid' },
  { label: 'Expiring', value: 'expiring' },
  { label: 'Expired', value: 'expired' },
  { label: 'None', value: 'none' },
];

const lifecycleStatusOptions = [
  { label: 'Prospect', value: 'prospect' },
  { label: 'Onboarding', value: 'onboarding' },
  { label: 'Approved', value: 'approved' },
  { label: 'Active', value: 'active' },
  { label: 'Probation', value: 'probation' },
  { label: 'Suspended', value: 'suspended' },
  { label: 'Terminated', value: 'terminated' },
];

function GroupTextField({
  control,
  name,
  label,
  optional = true,
  type = 'text',
  placeholder,
}: {
  control: Control<SupplierFormValues>;
  name: FieldPath<SupplierFormValues>;
  label: string;
  optional?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Input
          {...field}
          value={(field.value as string) ?? ''}
          label={label}
          type={type}
          placeholder={placeholder}
          optional={optional}
        />
      )}
    />
  );
}

function GroupCheckbox({
  control,
  name,
  label,
}: {
  control: Control<SupplierFormValues>;
  name: FieldPath<SupplierFormValues>;
  label: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <label className='flex items-center gap-2 font-inter text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={!!field.value}
            onChange={(event) => field.onChange(event.target.checked)}
            className='h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'
          />
          {label}
        </label>
      )}
    />
  );
}

export const emptyLocation: SupplierLocation = {
  locationId: '',
  address: '',
  type: '',
  line1: '',
  line2: '',
  city: '',
  county: '',
  country: '',
  postcode: '',
  coordinates: {
    lon: 0,
    lat: 0,
  },
  isPrimary: false,
  isActive: true,
};

interface SupplierFormFieldsProps {
  control: Control<SupplierFormValues>;
  formState: FormState<SupplierFormValues>;
  setValue: UseFormSetValue<SupplierFormValues>;
  clearErrors: UseFormClearErrors<SupplierFormValues>;
}

export function SupplierFormFields({
  control,
  formState,
  setValue,
  clearErrors,
}: SupplierFormFieldsProps) {
  const authHttp = useAuthHttp();
  const [optionsMap, setOptionsMap] = useState<Record<number, any[]>>({});
  const [inputValuesMap, setInputValuesMap] = useState<
    Record<number, string | null>
  >({});
  const [skuOptionsMap, setSkuOptionsMap] = useState<
    Record<number, AvailableSKU[]>
  >({});
  const [skuInputMap, setSkuInputMap] = useState<Record<number, string>>({});
  const [loadingSkusMap, setLoadingSkusMap] = useState<Record<number, boolean>>(
    {}
  );
  const skuControllersRef = useRef<Record<number, AbortController>>({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'locations',
  });

  const {
    fields: suppliedSkuFields,
    append: appendSuppliedSku,
    remove: removeSuppliedSku,
  } = useFieldArray({ control, name: 'suppliedSkus' });

  const watchedSuppliedSkus = useWatch({ control, name: 'suppliedSkus' }) ?? [];

  const fetchSupplierSkuOptions = async (query: string, index: number) => {
    if (query.length < 2) return;
    skuControllersRef.current[index]?.abort();
    const controller = new AbortController();
    skuControllersRef.current[index] = controller;
    setLoadingSkusMap((prev) => ({ ...prev, [index]: true }));
    try {
      const { data } = await authHttp.post(
        LIST_SKUS_URL,
        {
          searchQuery: query,
          isPalletisingRequired: true,
          pagination: { lastItemSort: null, itemsPerPage: 25 },
        },
        { signal: controller.signal }
      );
      setSkuOptionsMap((prev) => ({ ...prev, [index]: data.skus ?? [] }));
    } catch (error) {
      console.error('Error fetching SKUs:', error);
    } finally {
      setLoadingSkusMap((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleSupplierSkuSelect = (value: any, index: number) => {
    if (!value) {
      setSkuInputMap((prev) => ({ ...prev, [index]: '' }));
      setValue(`suppliedSkus.${index}.inventorySkuId`, '');
      setValue(`suppliedSkus.${index}.skuCode`, '');
      setValue(`suppliedSkus.${index}.productName`, '');
      return;
    }
    const sku = (skuOptionsMap[index] ?? []).find(
      (option) => option.skuId === value.value
    );
    if (!sku) return;
    setSkuInputMap((prev) => ({ ...prev, [index]: sku.skuId }));
    setValue(`suppliedSkus.${index}.inventorySkuId`, sku.inventorySkuId);
    setValue(`suppliedSkus.${index}.skuCode`, sku.skuId);
    setValue(
      `suppliedSkus.${index}.productName`,
      sku.productDetails?.name ?? sku.name
    );
  };

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({ control, name: 'contacts' });

  const {
    fields: certificationFields,
    append: appendCertification,
    remove: removeCertification,
  } = useFieldArray({ control, name: 'compliance.certifications' });

  const {
    fields: insuranceFields,
    append: appendInsurance,
    remove: removeInsurance,
  } = useFieldArray({ control, name: 'compliance.insurancePolicies' });

  const watchedLocations = useWatch({ control, name: 'locations' }) ?? [];

  const linkedLocationOptions = [
    { label: '— None —', value: '' },
    ...watchedLocations
      .filter((location) => location?.locationId)
      .map((location, index) => ({
        label: location?.address || `Location ${index + 1}`,
        value: location?.locationId || '',
      })),
  ];

  const handleFetchLocationSuggestions = (input: string, index: number) => {
    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items.map(
            (item: {
              id: any;
              address: { label: string; city: string; countryName: string };
            }) => ({
              id: item.id,
              label: item.address.label,
              city: item.address.city,
              country: item.address.countryName,
            })
          );
          setOptionsMap((prev) => ({ ...prev, [index]: items }));
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  };

  const handleLocationOptionSelect = async (value: any, index: number) => {
    if (!value) {
      setInputValuesMap((prev) => ({ ...prev, [index]: '' }));
      setValue(`locations.${index}.address`, '');
      setValue(`locations.${index}.line1`, '');
      setValue(`locations.${index}.city`, '');
      setValue(`locations.${index}.country`, '');
      setValue(`locations.${index}.postcode`, '');
      setValue(`locations.${index}.coordinates`, { lat: 0, lon: 0 });
      return;
    }

    if (value.id) {
      setInputValuesMap((prev) => ({ ...prev, [index]: value.label }));
      setValue(`locations.${index}.address`, value.label);
      setValue(`locations.${index}.line1`, value.label);
      setValue(`locations.${index}.city`, value.city || '');
      setValue(`locations.${index}.country`, value.country || '');
      clearErrors(`locations.${index}.address`);
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
        );

        const data = await response.json();

        if (data) {
          const { position, address } = data;

          if (position) {
            const { lat, lng: lon } = position;
            setValue(`locations.${index}.coordinates`, { lat, lon });
          }

          if (address) {
            setValue(`locations.${index}.city`, address.city || '');
            setValue(`locations.${index}.country`, address.countryName || '');
            setValue(`locations.${index}.postcode`, address.postalCode || '');
          }
        }
      } catch (error) {
        console.error('Error fetching position information:', error);
      }
    }
  };

  const handleAddLocation = () => {
    append({ ...emptyLocation });
  };

  const handleRemoveLocation = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <div className='flex flex-col space-y-6'>
      <section className='space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <div>
          <h2 className='font-inter text-lg font-semibold leading-7 text-gray-900'>
            Supplier details
          </h2>
          <p className='mt-0.5 font-inter text-sm text-gray-500'>
            Who this supplier is, the person to reach, and their head office.
          </p>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Controller
            name='supplierId'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Supplier ID'
                placeholder='Enter supplier ID'
                isInvalid={!!formState.errors.supplierId}
                validationMessage={formState.errors.supplierId?.message}
              />
            )}
          />
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Supplier Name'
                placeholder='Enter supplier name'
                isInvalid={!!formState.errors.name}
                validationMessage={formState.errors.name?.message}
              />
            )}
          />
          <Controller
            name='type'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Supplier Type'
                placeholder='e.g. Manufacturer, Co-packer'
                isInvalid={!!formState.errors.type}
                validationMessage={formState.errors.type?.message}
              />
            )}
          />
          <Controller
            name='contactName'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Contact Name'
                placeholder='Enter contact name'
                isInvalid={!!formState.errors.contactName}
                validationMessage={formState.errors.contactName?.message}
              />
            )}
          />
          <Controller
            name='contactEmail'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Contact Email'
                placeholder='name@company.com'
                type='email'
                isInvalid={!!formState.errors.contactEmail}
                validationMessage={formState.errors.contactEmail?.message}
              />
            )}
          />
          <Controller
            name='contactNumber'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='Contact Number'
                placeholder='Enter contact number'
                type='tel'
                optional
                isInvalid={!!formState.errors.contactNumber}
                validationMessage={formState.errors.contactNumber?.message}
              />
            )}
          />
          <Controller
            name='hqId'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='HQ ID'
                placeholder='Enter HQ ID'
                optional
                isInvalid={!!formState.errors.hqId}
                validationMessage={formState.errors.hqId?.message}
              />
            )}
          />
          <Controller
            name='hqName'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label='HQ Name'
                placeholder='Enter HQ name'
                optional
                isInvalid={!!formState.errors.hqName}
                validationMessage={formState.errors.hqName?.message}
              />
            )}
          />
        </div>
      </section>

      <section className='space-y-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <div>
          <h2 className='font-inter text-lg font-semibold leading-7 text-gray-900'>
            Locations
          </h2>
          <p className='mt-0.5 font-inter text-sm text-gray-500'>
            Every site this supplier ships from. Add one for each — at least one
            is required.
          </p>
        </div>
        <div className='space-y-4'>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              {fields.length > 1 && (
                <button
                  type='button'
                  onClick={() => handleRemoveLocation(index)}
                  className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                  aria-label='Remove location'
                >
                  <XMarkIcon className='h-5 w-5' />
                </button>
              )}
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Location {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <Controller
                  name={`locations.${index}.locationId`}
                  control={control}
                  render={({ field: locationField }) => (
                    <Input
                      {...locationField}
                      label='Location ID'
                      placeholder='Enter location ID'
                      optional
                      isInvalid={
                        !!formState.errors.locations?.[index]?.locationId
                      }
                      validationMessage={
                        formState.errors.locations?.[index]?.locationId?.message
                      }
                    />
                  )}
                />
                <Controller
                  name={`locations.${index}.type`}
                  control={control}
                  render={({ field: typeField }) => (
                    <Select
                      label='Type'
                      optional
                      placeholder='Select type'
                      options={locationTypeOptions}
                      selected={
                        locationTypeOptions.find(
                          (option) => option.value === typeField.value
                        ) ?? null
                      }
                      setSelected={(option) => typeField.onChange(option.value)}
                    />
                  )}
                />
                <Controller
                  name={`locations.${index}.address`}
                  control={control}
                  render={({ field: addressField }) => (
                    <Autocomplete
                      label='Address'
                      options={optionsMap[index] || []}
                      value={inputValuesMap[index] ?? addressField.value}
                      onInputChange={(newInputValue) => {
                        setInputValuesMap((prev) => ({
                          ...prev,
                          [index]: newInputValue,
                        }));
                        handleFetchLocationSuggestions(newInputValue, index);
                      }}
                      onChange={(value) => {
                        handleLocationOptionSelect(value, index);
                      }}
                      isInvalid={!!formState.errors.locations?.[index]?.address}
                      validationMessage={
                        formState.errors.locations?.[index]?.address
                          ?.message as string
                      }
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.line1`}
                  label='Address line 1'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.line2`}
                  label='Address line 2'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.city`}
                  label='City'
                />
                <GroupTextField
                  control={control}
                  name={`locations.${index}.county`}
                  label='County'
                />
              </div>
              <div className='mt-3 flex flex-wrap gap-6'>
                <GroupCheckbox
                  control={control}
                  name={`locations.${index}.isPrimary`}
                  label='Primary location'
                />
                <GroupCheckbox
                  control={control}
                  name={`locations.${index}.isActive`}
                  label='Active location'
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type='button'
          onClick={handleAddLocation}
          className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
        >
          <PlusIcon className='h-4 w-4' />
          Add another location
        </button>
      </section>

      <CollapsibleFormSection
        title='Classification & sourcing'
        description='Master-data attributes that drive the supplier table columns and badges.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Controller
            name='sourceType'
            control={control}
            render={({ field }) => (
              <Select
                label='Source type'
                optional
                placeholder='Select source type'
                options={sourceTypeOptions}
                selected={
                  sourceTypeOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                setSelected={(option) => field.onChange(option.value)}
              />
            )}
          />
          <Controller
            name='lifecycleStatus'
            control={control}
            render={({ field }) => (
              <Select
                label='Lifecycle status'
                optional
                placeholder='Select lifecycle status'
                options={lifecycleStatusOptions}
                selected={
                  lifecycleStatusOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                setSelected={(option) => field.onChange(option.value)}
              />
            )}
          />
          <GroupTextField
            control={control}
            name='brandsSupplied'
            label='Brands supplied'
            placeholder='Comma-separated, e.g. Acme, Zenith'
          />
          <GroupTextField
            control={control}
            name='coverageBranches'
            label='Coverage branches'
            placeholder='Comma-separated, e.g. London, Leeds'
          />
          <Controller
            name='certificationStatus'
            control={control}
            render={({ field }) => (
              <Select
                label='Certification status'
                optional
                placeholder='Select certification status'
                options={certStatusOptions}
                selected={
                  certStatusOptions.find(
                    (option) => option.value === field.value
                  ) ?? null
                }
                setSelected={(option) => field.onChange(option.value)}
              />
            )}
          />
          <GroupTextField
            control={control}
            name='certificationLabel'
            label='Certification label'
            placeholder='e.g. BRCGS AA'
          />
          <GroupTextField
            control={control}
            name='certificationExpiry'
            label='Certification expiry'
            type='date'
          />
          <Controller
            name='readinessGaps'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Readiness gaps'
                type='number'
                placeholder='e.g. 0'
                optional
                isInvalid={!!formState.errors.readinessGaps}
                validationMessage={formState.errors.readinessGaps?.message}
              />
            )}
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Legal entity'
        description='Registered company identifiers used for compliance and invoicing.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='legalEntity.legalName'
            label='Legal name'
          />
          <GroupTextField
            control={control}
            name='legalEntity.tradingName'
            label='Trading name'
          />
          <GroupTextField
            control={control}
            name='legalEntity.companyRegistrationNumber'
            label='Company registration number'
          />
          <GroupTextField
            control={control}
            name='legalEntity.vatNumber'
            label='VAT number'
          />
          <GroupTextField
            control={control}
            name='legalEntity.taxId'
            label='Tax ID'
          />
          <Controller
            name='legalEntity.jurisdictionCountry'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Jurisdiction country (ISO-2)'
                placeholder='e.g. GB'
                optional
                isInvalid={
                  !!formState.errors.legalEntity?.jurisdictionCountry
                }
                validationMessage={
                  formState.errors.legalEntity?.jurisdictionCountry?.message
                }
              />
            )}
          />
          <GroupTextField
            control={control}
            name='legalEntity.legalEntityType'
            label='Legal entity type'
          />
          <GroupTextField
            control={control}
            name='legalEntity.incorporationDate'
            label='Incorporation date'
            type='date'
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Contacts'
        description='People to reach at this supplier, with their role and linked site.'
      >
        <div className='space-y-4'>
          {contactFields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              <button
                type='button'
                onClick={() => removeContact(index)}
                className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                aria-label='Remove contact'
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Contact {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <GroupTextField
                  control={control}
                  name={`contacts.${index}.name`}
                  label='Name'
                />
                <Controller
                  name={`contacts.${index}.email`}
                  control={control}
                  render={({ field: emailField }) => (
                    <Input
                      {...emailField}
                      value={emailField.value ?? ''}
                      label='Email'
                      type='email'
                      optional
                      isInvalid={!!formState.errors.contacts?.[index]?.email}
                      validationMessage={
                        formState.errors.contacts?.[index]?.email?.message
                      }
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`contacts.${index}.phoneCountryCode`}
                  label='Phone country code'
                  placeholder='e.g. +44'
                />
                <GroupTextField
                  control={control}
                  name={`contacts.${index}.phoneNumber`}
                  label='Phone number'
                />
                <Controller
                  name={`contacts.${index}.role`}
                  control={control}
                  render={({ field: roleField }) => (
                    <Select
                      label='Role'
                      optional
                      placeholder='Select role'
                      options={contactRoleOptions}
                      selected={
                        contactRoleOptions.find(
                          (option) => option.value === roleField.value
                        ) ?? null
                      }
                      setSelected={(option) => roleField.onChange(option.value)}
                    />
                  )}
                />
                <Controller
                  name={`contacts.${index}.locationId`}
                  control={control}
                  render={({ field: locationField }) => (
                    <Select
                      label='Linked location'
                      optional
                      placeholder='Select location'
                      options={linkedLocationOptions}
                      selected={
                        linkedLocationOptions.find(
                          (option) => option.value === locationField.value
                        ) ?? null
                      }
                      setSelected={(option) =>
                        locationField.onChange(option.value)
                      }
                    />
                  )}
                />
              </div>
              <div className='mt-3'>
                <GroupCheckbox
                  control={control}
                  name={`contacts.${index}.isActive`}
                  label='Active contact'
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type='button'
          onClick={() =>
            appendContact({
              ...emptySupplierContact,
              contactId: crypto.randomUUID(),
            })
          }
          className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
        >
          <PlusIcon className='h-4 w-4' />
          Add contact
        </button>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Corporate hierarchy'
        description='Group structure this supplier belongs to.'
      >
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='corporateHierarchy.parentSupplierId'
            label='Parent supplier ID'
          />
          <GroupTextField
            control={control}
            name='corporateHierarchy.ultimateParentSupplierId'
            label='Ultimate parent supplier ID'
          />
          <Controller
            name='corporateHierarchy.hierarchyLevel'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                label='Hierarchy level'
                placeholder='e.g. 1'
                optional
                isInvalid={
                  !!formState.errors.corporateHierarchy?.hierarchyLevel
                }
                validationMessage={
                  formState.errors.corporateHierarchy?.hierarchyLevel?.message
                }
              />
            )}
          />
          <GroupTextField
            control={control}
            name='corporateHierarchy.regionalGrouping'
            label='Regional grouping'
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Compliance'
        description='Certifications, insurance cover, ESG rating and sanctions screening.'
      >
        <div className='space-y-4'>
          <div className='text-sm font-semibold text-gray-700'>
            Certifications
          </div>
          {certificationFields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              <button
                type='button'
                onClick={() => removeCertification(index)}
                className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                aria-label='Remove certification'
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Certification {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <GroupTextField
                  control={control}
                  name={`compliance.certifications.${index}.name`}
                  label='Name'
                  placeholder='e.g. BRCGS'
                />
                <GroupTextField
                  control={control}
                  name={`compliance.certifications.${index}.issuingBody`}
                  label='Issuing body'
                />
                <GroupTextField
                  control={control}
                  name={`compliance.certifications.${index}.certificateNumber`}
                  label='Certificate number'
                />
                <Controller
                  name={`compliance.certifications.${index}.status`}
                  control={control}
                  render={({ field: statusField }) => (
                    <Select
                      label='Status'
                      optional
                      placeholder='Select status'
                      options={certificationStatusOptions}
                      selected={
                        certificationStatusOptions.find(
                          (option) => option.value === statusField.value
                        ) ?? null
                      }
                      setSelected={(option) =>
                        statusField.onChange(option.value)
                      }
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`compliance.certifications.${index}.issueDate`}
                  label='Issue date'
                  type='date'
                />
                <Controller
                  name={`compliance.certifications.${index}.expiryDate`}
                  control={control}
                  render={({ field: expiryField }) => (
                    <Input
                      {...expiryField}
                      value={expiryField.value ?? ''}
                      label='Expiry date'
                      type='date'
                      optional
                      isInvalid={
                        !!formState.errors.compliance?.certifications?.[index]
                          ?.message
                      }
                      validationMessage={
                        formState.errors.compliance?.certifications?.[index]
                          ?.message as string
                      }
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`compliance.certifications.${index}.documentUrl`}
                  label='Document URL'
                />
              </div>
            </div>
          ))}
          <button
            type='button'
            onClick={() =>
              appendCertification({ ...emptySupplierCertification })
            }
            className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
          >
            <PlusIcon className='h-4 w-4' />
            Add certification
          </button>
        </div>
        <div className='space-y-4'>
          <div className='text-sm font-semibold text-gray-700'>
            Insurance policies
          </div>
          {insuranceFields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              <button
                type='button'
                onClick={() => removeInsurance(index)}
                className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                aria-label='Remove insurance policy'
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Insurance policy {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <GroupTextField
                  control={control}
                  name={`compliance.insurancePolicies.${index}.type`}
                  label='Type'
                  placeholder='e.g. Public liability'
                />
                <GroupTextField
                  control={control}
                  name={`compliance.insurancePolicies.${index}.provider`}
                  label='Provider'
                />
                <GroupTextField
                  control={control}
                  name={`compliance.insurancePolicies.${index}.policyNumber`}
                  label='Policy number'
                />
                <Controller
                  name={`compliance.insurancePolicies.${index}.coverageAmount`}
                  control={control}
                  render={({ field: amountField }) => (
                    <Input
                      {...amountField}
                      value={amountField.value ?? ''}
                      label='Coverage amount'
                      placeholder='e.g. 1000000'
                      optional
                      isInvalid={
                        !!formState.errors.compliance?.insurancePolicies?.[
                          index
                        ]?.coverageAmount
                      }
                      validationMessage={
                        formState.errors.compliance?.insurancePolicies?.[index]
                          ?.coverageAmount?.message
                      }
                    />
                  )}
                />
                <Controller
                  name={`compliance.insurancePolicies.${index}.currency`}
                  control={control}
                  render={({ field: currencyField }) => (
                    <Input
                      {...currencyField}
                      value={currencyField.value ?? ''}
                      label='Currency (ISO-3)'
                      placeholder='e.g. GBP'
                      optional
                      isInvalid={
                        !!formState.errors.compliance?.insurancePolicies?.[
                          index
                        ]?.currency
                      }
                      validationMessage={
                        formState.errors.compliance?.insurancePolicies?.[index]
                          ?.currency?.message
                      }
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`compliance.insurancePolicies.${index}.expiryDate`}
                  label='Expiry date'
                  type='date'
                />
                <GroupTextField
                  control={control}
                  name={`compliance.insurancePolicies.${index}.documentUrl`}
                  label='Document URL'
                />
              </div>
            </div>
          ))}
          <button
            type='button'
            onClick={() =>
              appendInsurance({ ...emptySupplierInsurancePolicy })
            }
            className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
          >
            <PlusIcon className='h-4 w-4' />
            Add insurance policy
          </button>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <GroupTextField
            control={control}
            name='compliance.esgRating'
            label='ESG rating'
            placeholder='e.g. A'
          />
          <GroupTextField
            control={control}
            name='compliance.esgAssessmentDate'
            label='ESG assessment date'
            type='date'
          />
          <GroupTextField
            control={control}
            name='compliance.sanctionsCheckStatus'
            label='Sanctions check status'
          />
          <GroupTextField
            control={control}
            name='compliance.sanctionsCheckedAt'
            label='Sanctions checked at'
            type='date'
          />
        </div>
      </CollapsibleFormSection>

      <CollapsibleFormSection
        title='Supplied SKUs & terms'
        description='Link the SKUs this supplier provides and the ordering terms for each.'
      >
        <div className='space-y-4'>
          {suppliedSkuFields.map((field, index) => (
            <div
              key={field.id}
              className='relative rounded-lg border border-gray-200 bg-gray-50/60 p-4'
            >
              <button
                type='button'
                onClick={() => removeSuppliedSku(index)}
                className='absolute right-3 top-3 text-gray-400 transition-colors hover:text-red-500'
                aria-label='Remove supplied SKU'
              >
                <XMarkIcon className='h-5 w-5' />
              </button>
              <div className='mb-3 text-sm font-semibold text-gray-700'>
                Supplied SKU {index + 1}
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <Controller
                  name={`suppliedSkus.${index}.skuCode`}
                  control={control}
                  render={({ field: skuField }) => (
                    <Autocomplete
                      label='SKU'
                      options={(skuOptionsMap[index] ?? []).map((sku) => ({
                        id: sku.inventorySkuId,
                        label: `${sku.skuId} - ${sku.name}`,
                        value: sku.skuId,
                      }))}
                      loading={!!loadingSkusMap[index]}
                      placeholder='Start typing to search SKUs...'
                      value={skuInputMap[index] ?? skuField.value ?? ''}
                      onInputChange={(newInputValue) => {
                        setSkuInputMap((prev) => ({
                          ...prev,
                          [index]: newInputValue,
                        }));
                        fetchSupplierSkuOptions(newInputValue, index);
                      }}
                      onChange={(value) => handleSupplierSkuSelect(value, index)}
                    />
                  )}
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.supplierSkuCode`}
                  label='Supplier SKU code'
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.leadTimeMeanDays`}
                  label='Lead time mean (days)'
                  type='number'
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.leadTimeStdDays`}
                  label='Lead time std (days)'
                  type='number'
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.minimumOrderQuantity`}
                  label='MOQ'
                  type='number'
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.orderMultiple`}
                  label='Order multiple'
                  type='number'
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.orderMultipleUnit`}
                  label='Order multiple unit'
                  placeholder='e.g. cases'
                />
                <GroupTextField
                  control={control}
                  name={`suppliedSkus.${index}.incoterms`}
                  label='Incoterms'
                  placeholder='e.g. FOB'
                />
              </div>
              {watchedSuppliedSkus[index]?.productName && (
                <p className='mt-2 text-xs text-gray-500'>
                  Product: {watchedSuppliedSkus[index]?.productName}
                </p>
              )}
              <div className='mt-3'>
                <GroupCheckbox
                  control={control}
                  name={`suppliedSkus.${index}.isPreferred`}
                  label='Preferred supplier for this SKU'
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type='button'
          onClick={() => appendSuppliedSku({ ...emptySupplierSkuLink })}
          className='inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary'
        >
          <PlusIcon className='h-4 w-4' />
          Add supplied SKU
        </button>
      </CollapsibleFormSection>
    </div>
  );
}
